/**
 * Turn a table into a CSV file the browser downloads.
 *
 * Client-side on purpose: there is no endpoint to add, and `lib/api.ts` stays the
 * only module that calls `fetch`.
 *
 * Two details that are always reported as bugs when they are missing:
 *
 * - **The BOM.** Without a `\uFEFF` prefix Excel reads the file as Latin-1 and every Czech
 *   diacritic becomes mojibake.
 * - **The formula guard.** Excel executes a cell starting with `=`, `+`, `-` or
 *   `@`, so an article titled "— Rytmus" is a live injection vector, not a title.
 */

const NEEDS_QUOTING = /[",;\n\r]/;
const FORMULA_START = /^[=+\-@]/;

export type CsvCell = string | number | null | undefined;

function escapeCell(cell: CsvCell): string {
  if (cell === null || cell === undefined) return "";
  const text = String(cell);
  const guarded = FORMULA_START.test(text) ? `'${text}` : text;
  if (!NEEDS_QUOTING.test(guarded)) return guarded;
  return `"${guarded.replaceAll('"', '""')}"`;
}

export function toCsv(rows: ReadonlyArray<ReadonlyArray<CsvCell>>): string {
  return rows.map((row) => row.map(escapeCell).join(",")).join("\r\n");
}

export function downloadCsv(filename: string, csv: string): void {
  // The byte-order mark goes first, ahead of the header row.
  const blob = new Blob([`\uFEFF${csv}`], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick: revoking synchronously can beat the download in
  // some browsers and produce an empty file.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
